<template>
  <div class="WizardForm">
    <div class="mainFrame">
      <HeaderBar class="headerBar">
        <slot name="headerRight"></slot>
      </HeaderBar>
      <div v-if="submissionStatus === 'submission-confirmed'" class="thankYouWrapper">
        <ThankYou :requestType="options.requestType" v-on="{ formDone }" />
      </div>
      <div v-else class="slides" ref="slides">
        <div v-for="{ key, component, content }, index in slideProps"
          :key="key"
          class="slide"
          :class="slidePositions[index]"
        >
          <component v-if="content"
            :is="component"
            v-bind="{ content, fieldValues }"
            :formOptions="options"
            :isStartingSlide="index === startingSlideIndex"
            :isCurrentSlide="index === currentSlideIndex"
          />
          <Sidebar v-if="index === currentSlideIndex && sidebarContent"
            :content="sidebarContent"
            class="mobileSidebar"
          />
        </div>
      </div>
      <ProgressBar v-if="submissionStatus === 'not-submitted'"
        v-bind="{ progress }"
        class="progressBar"
      />
    </div>
    <div v-if="submissionStatus !== 'submission-confirmed' && sidebarContent"
      class="sidebarFrame"
    >
      <Sidebar :content="sidebarContent" class="desktopSidebar" />
    </div>
  </div>
</template>

<script>
import submitForm from '~/lib/submitForm.js'
import getCountryData from '~/lib/countryData.js'
import slidesConfig from './slidesConfig.js'
import thankYouPaths from './data/thank-you-paths.json'
import pushWizardFormEvent from './util/pushWizardFormEvent.js'

import HeaderBar from './elems/HeaderBar.vue'
import ProgressBar from './elems/ProgressBar.vue'
import ThankYou from './elems/ThankYou.vue'
import Sidebar from './elems/Sidebar.vue'

export default {
  components: {
    HeaderBar,
    ProgressBar,
    Sidebar,
    ThankYou,
  },
  inject: ['locale'],
  props: {
    options: Object,
    submissionMeta: Object,
    onFormDone: Function,
  },
  provide() {
    return {
      updateField: (name, value, sync) => {
        this.fieldValues[name] = value
        if (sync === 'sync' && typeof localStorage !== 'undefined') {
          localStorage.fieldValues = JSON.stringify(this.fieldValues)
        }
      },
      nextSlide: () => {
        this.pushEvent('wizard_form_next_slide')
        this.currentSlideIndex = this.slidesEnabled.findIndex(
          (isEnabled, index) => isEnabled && index > this.currentSlideIndex
        )
        this.$refs.slides.scrollTo({ top: 0, behavior: 'smooth' })
      },
      previousSlide: () => {
        this.pushEvent('wizard_form_previous_slide')
        this.currentSlideIndex = this.slidesEnabled.findLastIndex(
          (isEnabled, index) => isEnabled && index < this.currentSlideIndex
        )
        this.$refs.slides.scrollTo({ top: 0, behavior: 'smooth' })
      },
      submitLead: () => {
        const { locale } = this
        const countryData = getCountryData(locale)
        const submissionData = {
          ...this.fieldValues,
          ...this.submissionMeta,
          country: countryData?.countryName,
          requestType: this.options.requestType,
        }
        const variant = { inseego: '5g', inseegoFleet: 'fleet' }[this.options.brand]
        this.submissionStatus = 'awaiting-confirmation'
        submitForm({ variant, locale }, submissionData).then(
          (response) => {
            if (response.ok) {
              this.pushEvent('wizard_form_conversion')
              const thankYouPath = thankYouPaths[this.locale]?.[this.options.requestType]?.[variant]
              if (thankYouPath) {
                window.location = thankYouPath
              } else {
                this.submissionStatus = 'submission-confirmed'
              }
            }
            else {
              throw new Error(`HTTP error! status: ${response.status}`)
            }
          }
        )
      },
    }
  },
  data() {
    return {
      fieldValues: {},
      currentSlideIndex: -1,
      submissionStatus: '',
    }
  },
  computed: {
    slideProps() {
      return slidesConfig.map(
        ({ key, component, content }) => ({ key, component, content: content[this.locale] })
      )
    },
    slidesEnabled() {
      return slidesConfig.map(
        (slide) => slide.content[this.locale] && slide.when(this.options, this.fieldValues)
      )
    },
    slidePositions() {
      return slidesConfig.map(
        (_slide, index) => {
          if (this.currentSlideIndex >= 0) {
            switch (Math.sign(index - this.currentSlideIndex)) {
            case -1:
              return 'past'
            case 0:
              return 'current'
            case +1:
              return 'future'
            }
          }
        }
      )
    },
    startingSlideIndex() {
      return this.slidesEnabled.findIndex(Boolean)
    },
    progress() {
      if (this.currentSlideIndex === -1) {
        return 0
      }
      const count = this.slidesEnabled.slice(0, this.currentSlideIndex).filter(Boolean).length
      const total = this.slidesEnabled.filter(Boolean).length
      return (count + 1) / total
    },
    sidebarContent() {
      if (this.currentSlideIndex >= 0) {
        const configValue = slidesConfig[this.currentSlideIndex]?.sidebarContent
        const sidebarContent = (
          typeof configValue === 'function' ? configValue(this.options) : configValue
        )?.[this.locale]
        if (sidebarContent) {
          if (Array.isArray(sidebarContent)) {
            const index = Math.floor(sidebarContent.length * Math.random())
            return sidebarContent[index]
          }
          return sidebarContent
        }
      }
    },
  },
  methods: {
    pushEvent(eventName) {
      const { incentive } = this.submissionMeta
      const stepName = slidesConfig[this.currentSlideIndex].key
      pushWizardFormEvent(eventName, incentive, this.options, this.fieldValues, stepName)
    },
    resetForm() {
      this.submissionStatus = 'not-submitted'
      this.currentSlideIndex = this.slidesEnabled.findIndex(Boolean)
    },
    formDone() {
      this.resetForm()
      this.onFormDone?.()
    },
  },
  mounted() {
    // Fetch field values from local storage
    if (localStorage.fieldValues) {
      const storedFieldValues = JSON.parse(localStorage.fieldValues)
      Object.assign(this.fieldValues, storedFieldValues)
    }
    // Reset local form state
    this.resetForm()
  },
}
</script>

<style scoped>

.mainFrame {
  --wizard-gutter-width-left: var(--gutter-width);
  --wizard-gutter-width-right: var(--gutter-width);
  height: 100dvh;
  display: flex;
  flex-direction: column;
}

.slides {
  flex: 1;
  display: grid;
  padding-left: var(--wizard-gutter-width-left);
  padding-right: var(--wizard-gutter-width-right);
  padding-bottom: var(--spacing-9);
  overflow-x: hidden;
  overflow-y: auto;
}

.slide {
  grid-row: 1;
  grid-column: 1;
  position: relative;
  left: 0;
  opacity: 0;
  transition: all 0.5s;
}

.slide.current {
  opacity: 1;
}

.slide.future, .slide.past {
  pointer-events: none;
}

.slide.future {
  left: 100%;
}

.slide.past {
  left: -100%;
}

.mobileSidebar {
  margin-top: var(--spacing-9);
  padding: var(--spacing-8);
  background-color: var(--bg-color-pale-blue);
}

.sidebarFrame {
  display: none;
}

.thankYouWrapper {
  flex: 1;
  overflow-y: auto;
}

@media (--screen-lg) {

  .WizardForm {
    display: flex;
  }

  .mainFrame:has(+ .sidebarFrame) {
    --wizard-gutter-width-right: var(--spacing-8);
  }

  .mainFrame {
    flex-grow: 1;
  }

  .sidebarFrame {
    flex: 0 0 35%;
    display: flex;
    align-items: center;
    padding: var(--spacing-10);
    background-color: var(--bg-color-pale-blue);
  }

  .mobileSidebar {
    display: none;
  }
}

@media (--screen-xl) {
  .sidebarFrame {
    flex-basis: 25%;
  }
}
</style>
